import { API_VERSION, CHARACTER_VISIBILITY, NotificationType, Screen, useTranslation, useUserSettings } from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { IconVariants } from "@next-platform/client-common-spicy-ui";
import { ConversationSortOrder, GetConversationsResponse } from "@next-platform/packages-ts-types";
import { useCallback, useEffect, useRef, useState } from "react";

import { useApi } from "@/components";
import { MY_CHATS_CONVERSATION_LIMIT, ROUTES, WHITE_LABEL_API_VERSION } from "@/constants";
import useAuth from "@/services/useAuth";
import { IChatsConversation, ILoadConversationsProps } from "@/types";
import { getBusinessRouteScreen } from "@/utils";

import useLocalizedLocation from "./useLocalizedLocation";
import useSharedChats from "./useSharedChats";
import useToast from "./useToast";

const recentChatsLogger = appLogger.withTag("useRecentChats");

export function useRecentChats() {
  const { pagePath } = useLocalizedLocation();
  const { displayToast } = useToast();
  const { onUserLikebot, likedBots } = useUserSettings();
  const { isAuthenticated } = useAuth();
  const [lastCharacterId, setLastCharacterId] = useState<string | null>(null);
  const refFetchId = useRef(0);
  const { t } = useTranslation();
  const {
    conversations,
    filteredConversations,
    setConversations,
    selectedConversation,
    setSelectedConversation,
    isChangeTitleModalVisible,
    setIsChangeTitleModalVisible,
    isRemoveConversationModalVisible,
    setIsRemoveConversationModalVisible,
    onUserRemoveConversation,
    onUpdateConversationTitle,
    convertTime,
    isRemoveConversationLoading,
    isChangeTitleLoading,
    getCommonDropdownListItems,
    handleSearch,
    handleClearSearch,
    searchQuery,
    sortOrder,
    isSortRefetching,
    isLoadingMoreConversations,
    getActiveSortOrder,
    markSortApplied,
    setPageOneInFlight,
    isPageOneBlockingLoadMore,
    handleSortChange: handleSharedSortChange,
    runLoadMore,
    multiSelectProps,
  } = useSharedChats();

  const { jsonApiService, callApi: callApiForConversations, isLoading: isConversationsLoading } = useApi<GetConversationsResponse>();
  const isLoadMoreDisabled = isConversationsLoading || isSortRefetching || isLoadingMoreConversations;

  const handleLikeBot = async (characterId: string) => {
    const screenName = getBusinessRouteScreen(pagePath);
    try {
      await onUserLikebot(characterId, screenName as Screen);
      const successMessage = likedBots?.includes(characterId)
        ? t("chatbot:toast.success.removedFromFavorites")
        : t("chatbot:toast.success.addedToFavorites");
      displayToast({ message: successMessage, type: NotificationType.success });
    } catch (likeBotError) {
      recentChatsLogger.error("Error while liking bot", { characterId }, likeBotError);
      displayToast({ message: t("error:fallbackError"), type: NotificationType.error });
    }
  };

  const getDropdownListItems = useCallback(
    (conversation: IChatsConversation) => {
      return [
        {
          label: t("common:viewSavedChats"),
          to: `${ROUTES.savedChats}/${conversation?.character_id}`,
          icon: IconVariants.LOGS,
        },
        {
          label: likedBots?.includes(conversation?.character_id) ? t("common:bot.unfavorite") : t("common:bot.favorite"),
          onPress: () => handleLikeBot(conversation?.character_id),
          icon: IconVariants.FAVORITE,
          isHidden: !isAuthenticated || !conversation?.character || conversation?.character?.visibility !== CHARACTER_VISIBILITY.PUBLIC,
        },
        getCommonDropdownListItems(conversation)?.changeTitle,
        getCommonDropdownListItems(conversation)?.removeConversation,
      ];
    },
    [likedBots, handleLikeBot, getCommonDropdownListItems, isAuthenticated, t],
  );

  const loadConversations = useCallback(
    async ({ limit = MY_CHATS_CONVERSATION_LIMIT, lastId, sort }: ILoadConversationsProps = {}) => {
      const isPageOne = !lastId;
      if (!isPageOne && isPageOneBlockingLoadMore()) {
        return;
      }

      const fetchId = ++refFetchId.current;
      const activeSort = sort ?? getActiveSortOrder();
      if (isPageOne) {
        setPageOneInFlight(true);
      }
      const params: Record<string, string | number> = { limit, sort: activeSort };
      if (lastId !== null && lastId !== "") {
        params.last_id = lastId as string;
      }

      try {
        const conversationResponse = await callApiForConversations(
          jsonApiService.fetchResource,
          "conversations",
          null,
          params,
          WHITE_LABEL_API_VERSION as API_VERSION,
        );

        if (fetchId !== refFetchId.current) {
          return;
        }

        if (isPageOne) {
          markSortApplied(activeSort);
        }

        if (conversationResponse) {
          setConversations((prev) =>
            lastId ? prev.concat(conversationResponse as IChatsConversation[]) : (conversationResponse as IChatsConversation[]),
          );
        }

        if (conversationResponse && conversationResponse.length > 0) {
          const lastCharacterCursorId = (conversationResponse as IChatsConversation[]).find((conversation) => conversation?.is_last_id)?.character_id;
          setLastCharacterId(lastCharacterCursorId ?? null);
        } else {
          setLastCharacterId(null);
        }
      } catch (error) {
        // Superseded requests must not reject — a stale failure would revert the sort dropdown.
        if (fetchId !== refFetchId.current) {
          return;
        }
        recentChatsLogger.error("Error when getting recent conversations:", error);
        throw error;
      } finally {
        if (isPageOne && fetchId === refFetchId.current) {
          setPageOneInFlight(false);
        }
      }
    },
    [
      callApiForConversations,
      getActiveSortOrder,
      isPageOneBlockingLoadMore,
      jsonApiService.fetchResource,
      markSortApplied,
      setConversations,
      setPageOneInFlight,
    ],
  );

  useEffect(() => {
    void loadConversations().catch(() => {
      displayToast({ message: t("error:fallbackError"), type: NotificationType.error });
    });
  }, [displayToast, loadConversations, t]);

  const handleSortChange = useCallback(
    (sort: ConversationSortOrder) => {
      handleSharedSortChange(sort, (nextSort) => loadConversations({ sort: nextSort }));
    },
    [handleSharedSortChange, loadConversations],
  );

  const loadMoreConversations = useCallback(() => {
    void runLoadMore(() => loadConversations({ lastId: lastCharacterId || undefined, sort: getActiveSortOrder() }));
  }, [getActiveSortOrder, lastCharacterId, loadConversations, runLoadMore]);

  return {
    conversations,
    filteredConversations,
    isConversationsLoading,
    isLoadingMoreConversations,
    isLoadMoreDisabled,
    isSortRefetching,
    isRemoveConversationLoading,
    isChangeTitleLoading,
    lastCharacterId,
    loadMoreConversations,
    onUserRemoveConversation,
    convertTime,
    getDropdownListItems,
    selectedConversation,
    setSelectedConversation,
    isChangeTitleModalVisible,
    setIsChangeTitleModalVisible,
    isRemoveConversationModalVisible,
    setIsRemoveConversationModalVisible,
    onUpdateConversationTitle,
    onSearch: handleSearch,
    onClearSearch: handleClearSearch,
    searchQuery,
    sortOrder,
    handleSortChange,
    multiSelectProps,
  };
}

export default useRecentChats;
